<template>
  <div class="col-12 card-wrapper">
    <div class="card mb-4">
      <!-- Card header -->
      <div class="card-header">
        <h3 class="mb-0">
          <span>{{ title }}</span>
          <badge v-if="formData.state" :type="getColor()" class="float-right">
            {{ formData.state.label }}
          </badge>
        </h3>
      </div>
      <!-- Card body -->
      <div class="card-body">
        <div v-if="campaign.articles && campaign.articles.length === 0" class="row mb-3">
          <div class="col-12 text-center">
            <h4 class="text-danger">La campaña seleccionada no posee articulos cargados.</h4>
          </div>
        </div>

        <!-- Form groups used in grid -->
        <form ref="form" class="needs-validation" @submit.prevent="saveOrder">
          <div v-if="newOrder" class="row mb-3">
            <div class="col-3 offset-9">
              <button class="btn btn-link btn-sm btn-fill btn-wd float-right" @click="goBack($event)">
                <i class="fas fa-chevron-left mr-1"></i>Volver
              </button>
            </div>
          </div>

          <order-info
            :campaign="campaign"
            :order-date="formData.date"
            :order-number="formData.code"
            :projection="projection"
            :producer="producer"
          />

          <hr />

          <div v-if="!edit && canEditOrder" class="row mb-0">
            <div class="col-3 offset-9">
              <button title="Editar" class="btn btn-link btn-sm btn-fill btn-wd float-right" @click="edit = true">
                <i class="fas fa-pen mr-1"></i>Editar
              </button>
            </div>
          </div>

          <order-header v-if="!editHeader" :order="order" />

          <div v-if="editHeader" class="row mt-4">
            <div v-if="saveErrors.length" class="col-12 mb-3">
              <div class="alert alert-danger mb-0" role="alert">
                <strong>No se pudo guardar el pedido.</strong>
                <div v-for="(error, index) in saveErrors" :key="index">{{ error }}</div>
              </div>
            </div>

            <div v-if="stockSourceEditingControlled && !newOrder" class="col-12 mb-4">
              <div class="stock-source-controlled-note" role="status">
                <i class="fas fa-random"></i>
                <div>
                  <strong>El origen y el código se cambian juntos</strong>
                  <span>
                    Usá el bloque anterior para validar stock y guardar ambos datos sin pasos intermedios. El resto de
                    los datos del pedido se puede editar acá normalmente.
                  </span>
                </div>
              </div>
            </div>

            <div v-else class="col-12 mb-4">
              <div class="stock-source-panel">
                <div class="stock-source-heading">
                  <div>
                    <span class="stock-source-kicker">ORIGEN DE LA MERCADERÍA</span>
                    <h4 class="mb-1">¿Desde dónde se prepara este pedido?</h4>
                    <p class="mb-0 text-muted">
                      El origen define qué disponibilidad se consulta para todos los artículos.
                    </p>
                  </div>
                  <span v-if="!hasItems && sourceSuggestionState === 'loading'" class="stock-source-status is-loading">
                    <i class="fas fa-spinner fa-spin mr-1"></i>Validando disponibilidad
                  </span>
                  <span
                    v-else-if="!hasItems && sourceSuggestionState === 'fallback'"
                    class="stock-source-status is-warning"
                  >
                    <i class="fas fa-exclamation-triangle mr-1"></i>Validación pendiente
                  </span>
                </div>

                <div class="stock-source-options" role="radiogroup" aria-label="Origen de la mercadería">
                  <button
                    type="button"
                    class="stock-source-option"
                    :class="{ active: formData.stockSource === STOCK_SOURCE_CENTRAL }"
                    :aria-pressed="formData.stockSource === STOCK_SOURCE_CENTRAL"
                    @click="selectStockSource(STOCK_SOURCE_CENTRAL)"
                  >
                    <span class="stock-source-icon"><i class="fas fa-warehouse"></i></span>
                    <span>
                      <strong>Casa Central</strong>
                      <small>Usa la disponibilidad general actual.</small>
                    </span>
                    <i v-if="formData.stockSource === STOCK_SOURCE_CENTRAL" class="fas fa-check-circle check"></i>
                  </button>

                  <button
                    type="button"
                    class="stock-source-option"
                    :class="{ active: formData.stockSource === STOCK_SOURCE_DISTRIBUTOR }"
                    :disabled="!stockSourcesSupported"
                    :aria-pressed="formData.stockSource === STOCK_SOURCE_DISTRIBUTOR"
                    @click="selectStockSource(STOCK_SOURCE_DISTRIBUTOR)"
                  >
                    <span class="stock-source-icon"><i class="fas fa-boxes"></i></span>
                    <span>
                      <strong>MiDepósito</strong>
                      <small>Usa el stock disponible de {{ distributorName }}.</small>
                    </span>
                    <i v-if="formData.stockSource === STOCK_SOURCE_DISTRIBUTOR" class="fas fa-check-circle check"></i>
                  </button>
                </div>

                <p v-if="hasItems" class="stock-source-help mb-0">
                  Al guardar, MiDepósito valida cada artículo contra el stock del distribuidor. Si falta saldo, el
                  pedido conserva su origen actual y muestra el artículo a corregir.
                </p>
                <p v-else-if="sourceSuggestionState === 'loading'" class="stock-source-help mb-0">
                  Estamos consultando el stock de {{ distributorName }}. Podés elegir el origen manualmente mientras
                  tanto.
                </p>
                <p
                  v-else-if="sourceSuggestionState === 'fallback'"
                  class="stock-source-help is-warning mb-0"
                  role="alert"
                >
                  No pudimos validar el stock a tiempo. Dejamos MiDepósito seleccionado y el sistema volverá a controlar
                  cada artículo al agregarlo.
                </p>
                <p v-else-if="sourceSuggestionState === 'suggested'" class="stock-source-help mb-0">
                  Origen sugerido según la disponibilidad actual de {{ distributorName }}. Podés cambiarlo antes de
                  agregar artículos.
                </p>
                <p v-else-if="!stockSourcesSupported" class="stock-source-help mb-0">
                  Esta campaña utiliza únicamente disponibilidad de Casa Central.
                </p>
              </div>
            </div>

            <div class="col-md-6 col-sm-6 col-xs-12">
              <div class="form-group">
                <label class="control-label"> Fecha Facturación (*) </label>

                <el-date-picker
                  v-model="formData.billingDate"
                  v-validate="modelValidations.billingDate"
                  align="center"
                  :editable="false"
                  format="dd-MM-yyyy"
                  name="billingDate"
                  placeholder="Fecha Facturación"
                  suffix-icon="fas fa-calendar-alt"
                  type="date"
                  value-format="yyyy-MM-dd"
                >
                </el-date-picker>

                <small v-show="billingDate.invalid" class="text-danger">
                  {{ getError('billingDate') }}
                </small>
              </div>

              <div class="form-group">
                <label class="control-label"> Fecha Primer Pago (*) </label>

                <el-date-picker
                  v-model="formData.firstPaymentDate"
                  v-validate="modelValidations.firstPaymentDate"
                  align="center"
                  :editable="false"
                  format="dd-MM-yyyy"
                  name="firstPaymentDate"
                  placeholder="Fecha Primer Pago"
                  suffix-icon="fas fa-calendar-alt"
                  type="date"
                  value-format="yyyy-MM-dd"
                >
                </el-date-picker>

                <small v-show="firstPaymentDate.invalid" class="text-danger">
                  {{ getError('firstPaymentDate') }}
                </small>
              </div>

              <div class="form-group">
                <label class="control-label"> Cantidad de Pagos (*) </label>

                <el-input
                  v-model="formData.payments"
                  v-validate="modelValidations.payments"
                  name="payments"
                  pattern="[0-9]+"
                  placeholder="Cantidad de Pagos"
                  type="number"
                >
                </el-input>

                <small v-show="payments.invalid" class="text-danger">
                  {{ getError('payments') }}
                </small>
              </div>
            </div>

            <div class="col-md-6 col-sm-6 col-xs-12">
              <div class="form-group">
                <label class="control-label"> Fecha Cotización Dólar (*) </label>

                <el-date-picker
                  v-model="formData.dollarQuoteDate"
                  v-validate="modelValidations.dollarQuoteDate"
                  align="center"
                  :editable="false"
                  :picker-options="datePickerOptions"
                  format="dd-MM-yyyy"
                  name="dollarQuoteDate"
                  placeholder="Fecha Cotización Dólar"
                  suffix-icon="fas fa-calendar-alt"
                  type="date"
                  value-format="yyyy-MM-dd"
                  @change="getDollarQuotation"
                >
                </el-date-picker>

                <small v-show="dollarQuoteDate.invalid" class="text-danger">
                  {{ getError('dollarQuoteDate') }}
                </small>
              </div>

              <div class="form-group">
                <label class="control-label"> Cotización Dólar (*) </label>
                <div class="row justify-content-center align-items-center">
                  <div class="col-md-6 col-sm-8 col-xs-12">
                    <el-select
                      v-model="formData.dollarQuotationType"
                      v-validate="modelValidations.dollarQuotationType"
                      name="dollarQuotationType"
                      placeholder="Seleccione Tipo de Cotización (*)"
                      autocomplete="off"
                      filterable
                      @change="changeDollarQuotationType"
                    >
                      <el-option
                        v-for="option in dollarOptions"
                        :key="option.value"
                        :label="option.label"
                        :value="option.value"
                      >
                      </el-option>
                    </el-select>
                  </div>

                  <div class="col-md-6 col-sm-8 col-xs-12">
                    <template v-if="$can('all', 'dollar_quotations')">
                      <el-input
                        v-model="formData.dollarQuote"
                        v-validate="'min_value:' + minValueDollarQuote + '|required'"
                        name="dollarQuote"
                        pattern="[0-9]+([\.,][0-9]+)?"
                        placeholder="Cotización Dólar"
                        step="0.01"
                        type="number"
                        suffix-icon="fas fa-dollar-sign"
                        @change="clearDollarQuotation"
                      >
                      </el-input>

                      <small v-show="dollarQuote.invalid" class="text-danger">
                        {{ getError('dollarQuote') }}
                      </small>
                    </template>

                    <h1 v-else class="font-weight-bolder">{{ getDollarQuote | toCurrencyARS }} .-</h1>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label class="control-label"> Observaciones </label>

                <el-input
                  v-model="formData.comment"
                  v-validate="modelValidations.comment"
                  :autosize="{ minRows: 5 }"
                  name="comment"
                  placeholder="Ingrese una observación"
                  type="textarea"
                >
                </el-input>

                <small v-show="comment.invalid" class="text-danger">
                  {{ getError('comment') }}
                </small>
              </div>
            </div>

            <div class="col-md-12 text-right">
              <base-button :loading="savingHeader" native-type="submit" type="primary">
                {{ savingHeader ? 'Guardando' : 'Siguiente' }}
              </base-button>
            </div>

            <order-inline-items-list
              v-if="hasItems && canEditItems"
              class="col-12"
              :items="formData.items"
              :order-id="orderId"
              @updated="replaceItem"
            />
          </div>
        </form>
      </div>
    </div>

    <div v-if="!editHeader" class="card mb-4">
      <div class="card-body">
        <order-item-form
          v-if="canEditItems"
          ref="itemForm"
          :campaign="campaign"
          :distributor-name="distributorName"
          :order="orderId"
          :projection="projection"
          :stock-source="formData.stockSource"
          @addItem="addItem"
        />

        <order-items-list
          v-if="hasItems"
          :add="orderOpen"
          :dollar-quote="getDollarQuote"
          :edit="canEditItems"
          :items="formData.items"
          :order-id="orderId"
          @deleted="handleItemsChanged"
          @edit="toggleEditItems"
        />

        <div class="row mt-3">
          <div v-if="canEditItems" class="col-md-12 text-right">
            <base-button v-if="hasItems" type="primary" @click="toggleEditItems">
              <span v-if="orderOpen">Siguiente</span>
              <span v-else>Guardar Cambios</span>
            </base-button>
          </div>
          <div v-else-if="orderOpen" class="col-md-12 text-right">
            <base-button v-if="hasItems" type="success" @click="confirmOrder">Confirmar Pedido</base-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Select, Option, DatePicker } from 'element-ui';
import moment from 'moment';
import { mapActions, mapState, mapGetters } from 'vuex';
import { mapFields } from 'vee-validate';

import OrdersService from '@/services/resources/orders.service';
import { getDollarQuote } from '@/util/orders';
import { buildControlledCorrectionOrderPatch } from '@/util/orderControlledCorrection';

import OrderItemForm from './OrderItemForm';
import OrderItemsList from '../List/OrderItemsList';
import OrderInlineItemsList from '../List/OrderInlineItemsList';
import OrderHeader from '../Show/OrderHeader';
import OrderInfo from '../Show/OrderInfo';
import { MANUAL } from '@/util/constants/dollarQuotationTypes';
import { STOCK_SOURCE_CENTRAL, STOCK_SOURCE_DISTRIBUTOR } from '@/util/constants/orders';

export default {
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
    OrderItemForm,
    OrderItemsList,
    OrderInlineItemsList,
    OrderInfo,
    OrderHeader,
  },

  props: {
    title: {
      type: String,
      default: 'Agregar un nuevo pedido',
    },

    orderId: {
      type: Number,
      default: 0,
    },

    producer: {
      type: Object,
      required: true,
    },

    campaign: {
      type: Object,
      required: true,
    },

    order: {
      type: Object,
      default: () => {
        return {
          code: null,
          date: null,
          firstPaymentDate: null,
          billingDate: null,
          payments: null,
          dollarQuote: null,
          dollarQuoteDate: null,
          dollarQuotationType: null,
          dollarQuotationId: null,
          comment: null,
          statusChangeComment: null,
          state: null,
          items: [],
        };
      },
    },

    projection: {
      type: Object,
      default: null,
    },

    stockSourceEditingControlled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      datePickerOptions: {
        disabledDate: this.disabledDate,
      },

      validated: false,
      edit: true,
      editItems: true,
      statesColors: [],
      sourceSuggestionState: 'idle',
      sourceManuallySelected: false,
      sourceSuggestionRequest: 0,
      saveErrors: [],
      savingHeader: false,
      STOCK_SOURCE_CENTRAL,
      STOCK_SOURCE_DISTRIBUTOR,
      formData: {
        code: this.order.code,
        date: this.order.date || new Date(),
        firstPaymentDate: this.order.firstPaymentDate,
        billingDate: this.order.billingDate,
        payments: this.order.payments,
        dollarQuote: this.order.dollarQuote,
        dollarQuoteDate: this.order.dollarQuoteDate,
        dollarQuotationType: this.order.dollarQuotationType,
        dollarQuotationId: this.order.dollarQuotationId,
        dollarQuotation: this.order.dollarQuotation,
        comment: this.order.comment,
        statusChangeComment: this.order.statusChangeComment,
        stockSource: this.order.stockSource || STOCK_SOURCE_CENTRAL,
        state: this.order.state,
        items: this.order.items,
      },

      modelValidations: {
        firstPaymentDate: {
          required: true,
        },

        billingDate: {
          required: true,
        },

        payments: {
          required: true,
          numeric: true,
        },

        dollarQuotationType: {
          required: true,
        },

        dollarQuotationId: {
          required: !this.$can('all', 'dollar_quotations'),
        },

        dollarQuoteDate: {
          required: true,
        },

        dollarQuote: {
          required: true,
        },

        comment: {},
        statusChangeComment: {
          required: true,
        },
      },
    };
  },

  computed: {
    ...mapState('dollarQuotation', ['availableDates', 'currentDollarQuotation']),

    ...mapGetters('config', ['dollarSelected', 'dollarOptions']),

    newOrder() {
      return this.orderId === 0;
    },

    editHeader() {
      return this.edit || this.newOrder;
    },

    canEditOrder() {
      return (this.$can('update', 'orders') && this.orderOpen) || this.$can('all', 'orders');
    },

    canEditItems() {
      return this.editItems && this.canEditOrder;
    },

    orderOpen() {
      return this.formData.state !== null && this.formData.state.name === OrdersService._STATE_OPEN;
    },

    hasItems() {
      return this.formData.items.length > 0;
    },

    distributorName() {
      return this.producer && this.producer.distributor ? this.producer.distributor.name : 'el distribuidor asignado';
    },

    stockSourcesSupported() {
      return this.campaign.stockSourceEnabled !== false;
    },

    getDollarQuote() {
      return getDollarQuote(this.formData);
    },

    minValueDollarQuote() {
      return this.formData.dollarQuotationType === MANUAL ? this.currentDollarQuotation.divisa : 0;
    },

    ...mapFields([
      'firstPaymentDate',
      'billingDate',
      'payments',
      'dollarQuote',
      'dollarQuoteDate',
      'dollarQuotationType',
      'dollarQuotationId',
      'comment',
    ]),
  },

  watch: {
    dollarSelected: {
      immediate: true,
      handler() {
        if (this.newOrder) {
          this.formData.dollarQuotationType = this.dollarSelected;
        }
      },
    },

    availableDates: {
      immediate: true,
      deep: true,
      handler(dates) {
        if (this.newOrder) {
          this.formData.dollarQuoteDate = dates.at(-1);
          this.getDollarQuotation();
        }
      },
    },
  },

  mounted() {
    this.fetchAvailableDates();

    this.statesColors = this.$services.resource('order_states').getColors();
    // seteo el modo de edicion para el form
    // cuando se crea un pedido se habilita la edicion
    // cuando se edita un pedido se habilita la vista de detalle
    this.edit = this.newOrder;
    this.suggestStockSource();
  },

  beforeDestroy() {
    this.sourceSuggestionRequest += 1;
  },

  methods: {
    ...mapActions('dollarQuotation', ['fetchAvailableDates']),

    selectStockSource(source) {
      this.sourceManuallySelected = true;
      this.sourceSuggestionState = 'manual';
      this.saveErrors = [];
      this.formData.stockSource = source;
    },

    async suggestStockSource() {
      if (!this.newOrder || this.hasItems) return;

      if (!this.stockSourcesSupported) {
        this.formData.stockSource = STOCK_SOURCE_CENTRAL;
        this.sourceSuggestionState = 'unsupported';
        return;
      }

      const requestId = ++this.sourceSuggestionRequest;
      this.formData.stockSource = STOCK_SOURCE_DISTRIBUTOR;
      this.sourceSuggestionState = 'loading';

      let timeoutId;
      const timeout = new Promise((resolve) => {
        timeoutId = setTimeout(() => resolve(null), 4000);
      });

      try {
        const response = await Promise.race([
          this.$services.resource('orders').stockSourceSuggestion({
            campaignId: this.campaign.id,
            producerId: this.producer.id,
          }),
          timeout,
        ]);

        if (!response) throw new Error('stock-source-suggestion-timeout');

        if (requestId !== this.sourceSuggestionRequest || this.sourceManuallySelected) return;

        this.formData.stockSource = response.data.data.suggestedSource;
        this.sourceSuggestionState = 'suggested';
      } catch (error) {
        if (requestId !== this.sourceSuggestionRequest || this.sourceManuallySelected) return;

        this.formData.stockSource = STOCK_SOURCE_DISTRIBUTOR;
        this.sourceSuggestionState = 'fallback';
      } finally {
        clearTimeout(timeoutId);
      }
    },

    changeDollarQuotationType(value) {
      this.formData.dollarQuotationType = value;
      this.formData.dollarQuotationId = this.formData.dollarQuotation ? this.formData.dollarQuotation.id : null;
      this.formData.dollarQuote = this.getDollarQuote;
    },

    clearDollarQuotation() {
      this.formData.dollarQuotationId = null;
      this.formData.dollarQuotation = null;
    },

    getDollarQuotation() {
      const selectedDate = moment(this.formData.dollarQuoteDate);

      this.$services
        .resource('dollar_quotations')
        .get({ 'filter[date]': selectedDate.format('YYYY-MM-DD') })
        .then((res) => res.data.data)
        .then(([quotation]) => {
          if (quotation) {
            this.formData.dollarQuotation = quotation;
            this.formData.dollarQuotationId = quotation.id;
            this.formData.dollarQuote = this.getDollarQuote;
          }
        });
    },

    getColor() {
      return this.statesColors[this.formData.state.id - 1];
    },

    addItem(item) {
      this.formData.items.push(item);
      this.$refs.itemForm.clean();
      this.$refs.itemForm.refreshAvailability();
      this.$emit('items-changed');
    },

    replaceItem(updatedItem) {
      const index = this.formData.items.findIndex((item) => item.id === updatedItem.id);
      if (index === -1) return;

      this.$set(this.formData.items, index, {
        ...this.formData.items[index],
        ...updatedItem,
        article: updatedItem.article || this.formData.items[index].article,
      });
      this.refreshAvailability();
      this.$emit('items-changed');
    },

    handleItemsChanged() {
      this.refreshAvailability();
      this.$emit('items-changed');
    },

    refreshAvailability() {
      if (this.$refs.itemForm) {
        this.$refs.itemForm.refreshAvailability();
      }
    },

    toggleEditItems() {
      this.editItems = !this.editItems;
    },

    async firstFormSubmit() {
      try {
        return await this.$validator.validateAll();
      } finally {
        this.validated = true;
      }
    },

    async saveOrder() {
      const valid = await this.firstFormSubmit();
      if (!valid || this.savingHeader) return;

      // Creation keeps its existing two-step flow. Existing orders use an
      // explicit lifecycle because Vue events do not return the listener's
      // promise: the header may close only after PATCH + full refetch succeed.
      if (this.newOrder) {
        this.$emit('save', this.formData);
        this.edit = false;
        return;
      }

      this.saveErrors = [];
      this.savingHeader = true;
      this.$emit('save', this.formData, {
        resolve: this.completeHeaderSave,
        reject: this.rejectHeaderSave,
      });
    },

    completeHeaderSave(order) {
      this.syncPersistedOrder(order);
      this.savingHeader = false;
      this.edit = false;
    },

    rejectHeaderSave(errors) {
      this.saveErrors = this.normalizeSaveErrors(errors);
      this.savingHeader = false;
    },

    syncPersistedOrder(order) {
      Object.assign(this.formData, {
        code: order.code,
        date: order.date,
        firstPaymentDate: order.firstPaymentDate,
        billingDate: order.billingDate,
        payments: order.payments,
        dollarQuote: order.dollarQuote,
        dollarQuoteDate: order.dollarQuoteDate,
        dollarQuotationType: order.dollarQuotationType,
        dollarQuotationId: order.dollarQuotationId,
        dollarQuotation: order.dollarQuotation,
        comment: order.comment,
        statusChangeComment: order.statusChangeComment,
        stockSource: order.stockSource || STOCK_SOURCE_CENTRAL,
        state: order.state,
        items: order.items || [],
      });
    },

    syncControlledCorrection(order) {
      Object.assign(this.formData, buildControlledCorrectionOrderPatch(order));
      this.saveErrors = [];
      this.$nextTick(this.refreshAvailability);
    },

    normalizeSaveErrors(errors) {
      if (Array.isArray(errors)) {
        return errors;
      }

      const response = errors && errors.response && errors.response.data;
      if (response && response.errors) {
        const messages = Object.keys(response.errors).reduce((all, field) => {
          const fieldErrors = response.errors[field];
          return all.concat(Array.isArray(fieldErrors) ? fieldErrors : [fieldErrors]);
        }, []);

        if (messages.length) return messages;
      }

      if (response && response.message) {
        return [response.message];
      }

      return ['No pudimos guardar los cambios. Revisá los datos e intentá nuevamente.'];
    },

    confirmOrder() {
      this.$emit('confirm', this.formData);
    },

    getError(name) {
      return this.errors.first(name);
    },

    isValid(name) {
      return this.validated && !this.errors.has(name);
    },

    goBack() {
      this.$emit('back');
    },

    disabledDate(date) {
      const dateFormatted = moment(date).format('YYYY-MM-DD');

      return !this.availableDates.includes(dateFormatted);
    },
  },
};
</script>

<style scoped>
.form-group .el-date-editor--date {
  width: 100%;
}

.stock-source-panel {
  padding: 1.25rem;
  border: 1px solid #d8dee8;
  border-left: 4px solid #f4c430;
  border-radius: 0.5rem;
  background: #f8fafc;
}

.stock-source-controlled-note {
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  padding: 1rem;
  border-left: 4px solid #2dce89;
  border-radius: 0.4rem;
  color: #344050;
  background: #f0fbf6;
}

.stock-source-controlled-note i {
  margin-top: 0.15rem;
  color: #147d57;
}

.stock-source-controlled-note strong,
.stock-source-controlled-note span {
  display: block;
}

.stock-source-controlled-note span {
  margin-top: 0.15rem;
  color: #536273;
  font-size: 0.85rem;
}

.stock-source-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.stock-source-kicker {
  color: #6b7280;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
}

.stock-source-options {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.stock-source-option {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  width: 100%;
  padding: 1rem;
  border: 1px solid #cfd6e1;
  border-radius: 0.45rem;
  color: #344050;
  text-align: left;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.stock-source-option:disabled {
  cursor: not-allowed;
  opacity: 0.78;
}

.stock-source-option:not(:disabled):hover {
  border-color: #6b7c93;
  box-shadow: 0 4px 12px rgb(31 45 61 / 8%);
  transform: translateY(-1px);
}

.stock-source-option.active {
  border-color: #172b4d;
  box-shadow: inset 0 0 0 1px #172b4d;
}

.stock-source-option strong,
.stock-source-option small {
  display: block;
}

.stock-source-option strong {
  margin-bottom: 0.15rem;
  font-size: 0.95rem;
}

.stock-source-option small {
  color: #6b7280;
  line-height: 1.35;
}

.stock-source-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.35rem;
  height: 2.35rem;
  flex: 0 0 2.35rem;
  border-radius: 0.35rem;
  color: #172b4d;
  background: #f4c430;
}

.stock-source-option .check {
  margin-left: auto;
  color: #2dce89;
}

.stock-source-status {
  align-items: center;
  border: 1px solid #d7dde5;
  border-radius: 999px;
  color: #536273;
  display: inline-flex;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 0.4rem 0.65rem;
  text-transform: uppercase;
}

.stock-source-status.is-loading {
  background: #f4f8fc;
  border-color: #bfd0e3;
  color: #244f7a;
}

.stock-source-status.is-warning {
  background: #fff8e1;
  border-color: #efcf73;
  color: #72520a;
}

.stock-source-help {
  margin-top: 0.75rem;
  color: #7b4b00;
  font-size: 0.78rem;
}

.stock-source-help.is-warning {
  background: #fff8e1;
  border-left-color: #d9a714;
  color: #5f4708;
}

@media (max-width: 767px) {
  .stock-source-heading,
  .stock-source-options {
    display: block;
  }

  .stock-source-option + .stock-source-option {
    margin-top: 0.65rem;
  }
}
</style>
